import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,

} from '@mui/material';
import ResponsiveDialog from './ResponsiveDialog';
import { useQuery } from '@tanstack/react-query';
import { LocationProvider, ProviderInterfaceThemeSettings, UpdateProviderRequest, PROVIDER_DELIVERY_MODE_OPTIONS } from '../types/provider';
import apiService from '../services/api';
import LogoUpload from './LogoUpload';
import ProviderUserManager from './ProviderUserManager';
import ProviderAddressAutocompleteMap from './ProviderAddressAutocompleteMap';
import { ProviderAddressFields } from './ProviderAddressAutocompleteMap';
import ProviderPlacesSection from './ProviderPlacesSection';
import ProviderDeletionSection from './ProviderDeletionSection';
import PartnerLogoCircleEditor from './PartnerLogoCircleEditor';
import { resolveProviderLogoUrl } from '../utils/resolveProviderLogoUrl';
import {
  DEFAULT_PARTNER_LOGO_CIRCLE_LAYOUT,
  normalizePartnerLogoCircleLayout,
  type PartnerLogoCircleLayout,
} from '../constants/partnerLogoCircle';

function buildAddressFromProvider(p: LocationProvider): ProviderAddressFields {
  const a = p.address;
  if (!a) {
    return {
      name: p.name || '',
      address: '',
      city: '',
      region: '',
      country: 'France',
      postal_code: '',
      latitude: undefined,
      longitude: undefined,
    };
  }
  return {
    name: a.name || '',
    address: a.address || '',
    city: a.city || '',
    region: a.region || '',
    country: a.country || 'France',
    postal_code: a.postal_code || '',
    latitude: a.latitude,
    longitude: a.longitude,
  };
}

function toProviderAddressFields(
  a: UpdateProviderRequest['address'] | undefined,
  p: LocationProvider
): ProviderAddressFields {
  const base = a ?? buildAddressFromProvider(p);
  return {
    ...base,
    name: base.name ?? '',
    latitude: base.latitude === null || base.latitude === undefined ? undefined : base.latitude,
    longitude: base.longitude === null || base.longitude === undefined ? undefined : base.longitude,
  };
}

interface EditProviderDialogProps {
  open: boolean;
  onClose: () => void;
  provider: LocationProvider | null;
  onSave: (provider: LocationProvider) => void;
  /** Met à jour la fiche en mémoire après auto-sauvegarde du cadrage logo. */
  onProviderPatch?: (provider: LocationProvider) => void;
  /** Après suppression définitive du partenaire. */
  onDeleted?: () => void;
}

const DEFAULT_INTERFACE_THEME: ProviderInterfaceThemeSettings = {
  preset: 'sherlook',
  primary: '#1976d2',
  secondary: '#dc004e',
  background: '#f4f6f8',
};

const THEME_PRESETS: Array<{ key: string; label: string; colors: ProviderInterfaceThemeSettings }> = [
  { key: 'sherlook', label: 'Sherlook', colors: { primary: '#1976d2', secondary: '#dc004e', background: '#f4f6f8' } },
  { key: 'forest', label: 'Foret', colors: { primary: '#2e7d32', secondary: '#8bc34a', background: '#f3f8f4' } },
  { key: 'sunset', label: 'Coucher de soleil', colors: { primary: '#ef6c00', secondary: '#ad1457', background: '#fff7f1' } },
  { key: 'night', label: 'Nuit', colors: { primary: '#283593', secondary: '#00838f', background: '#eef1f8' } },
];

const EditProviderDialog: React.FC<EditProviderDialogProps> = ({
  open,
  onClose,
  provider,
  onSave,
  onProviderPatch,
  onDeleted,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveTab(0);
    }
  }, [open, provider?.id]);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<UpdateProviderRequest>({
    name: '',
    description: '',
    website: '',
    phone: '',
    email: '',
    notification_recipient_email: '',
    notification_emails_enabled: true,
    private_announcements: false,
    visible_on_frontend: true,
    private_page_background_color: '#EBC41D',
    private_page_logo_layout: DEFAULT_PARTNER_LOGO_CIRCLE_LAYOUT,
    status: 'active',
    settings: {},
    monthly_fee: undefined,
    address: undefined,
  });
  const [logoUrlOverride, setLogoUrlOverride] = useState<string | null>(null);
  const [logoLayoutSaveError, setLogoLayoutSaveError] = useState('');
  const [logoLayoutSaving, setLogoLayoutSaving] = useState(false);
  const logoLayoutSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: dbProviderTypes, isLoading: loadingProviderTypes } = useQuery({
    queryKey: ['provider-types-entity'],
    queryFn: () => apiService.getProviderTypesFromEntity(),
    enabled: open,
  });

  const formLoadedRef = useRef<{ open: boolean; providerId: number | null }>({
    open: false,
    providerId: null,
  });

  useEffect(() => {
    if (!open) {
      formLoadedRef.current = { open: false, providerId: null };
      return;
    }
    if (!provider) return;

    const shouldLoadForm =
      !formLoadedRef.current.open || formLoadedRef.current.providerId !== provider.id;
    if (!shouldLoadForm) return;

    formLoadedRef.current = { open: true, providerId: provider.id };

    const settings = provider.settings as Record<string, unknown> | undefined;
    const layoutFromApi = normalizePartnerLogoCircleLayout(
      provider.private_page_logo_layout
        ?? settings?.private_page_logo_layout
        ?? DEFAULT_PARTNER_LOGO_CIRCLE_LAYOUT
    );
    setFormData({
      name: provider.name || '',
      slug: provider.slug ?? '',
      type: provider.provider_type?.slug || provider.type || undefined,
      provider_type_id: provider.provider_type?.id ?? undefined,
      description: provider.description || '',
      website: provider.website || '',
      phone: provider.phone || '',
      email: provider.email || '',
      notification_recipient_email: provider.notification_recipient_email ?? '',
      notification_emails_enabled: provider.notification_emails_enabled !== false,
      private_announcements: provider.private_announcements === true,
      visible_on_frontend: provider.visible_on_frontend !== false,
      private_page_background_color: provider.private_page_background_color || '#EBC41D',
      private_page_logo_layout: layoutFromApi,
      place_storage_mode_override: provider.place_storage_mode_override ?? null,
      status: provider.status || 'active',
      settings: provider.settings || {},
      monthly_fee: provider.monthly_fee || undefined,
      address: buildAddressFromProvider(provider),
    });
    setLogoUrlOverride(null);
    setLogoLayoutSaveError('');
  }, [open, provider]);

  useEffect(() => {
    return () => {
      if (logoLayoutSaveTimer.current) {
        clearTimeout(logoLayoutSaveTimer.current);
      }
    };
  }, []);

  const persistLogoLayout = useCallback(
    async (layout: PartnerLogoCircleLayout) => {
      if (!provider) return;
      const normalized = normalizePartnerLogoCircleLayout(layout);
      try {
        setLogoLayoutSaving(true);
        setLogoLayoutSaveError('');
        const updated = await apiService.updateProviderPrivatePageLogoLayout(
          provider.id,
          normalized,
          (provider.settings as Record<string, unknown> | undefined) ?? {}
        );
        const savedLayout = normalizePartnerLogoCircleLayout(
          updated.private_page_logo_layout
            ?? (updated.settings as Record<string, unknown> | undefined)?.private_page_logo_layout
            ?? normalized
        );
        setFormData((prev) => ({
          ...prev,
          private_page_logo_layout: savedLayout,
          settings: {
            ...((prev.settings as Record<string, unknown>) || {}),
            private_page_logo_layout: savedLayout,
          },
        }));
        onProviderPatch?.({
          ...provider,
          ...updated,
          private_page_logo_layout: savedLayout,
          settings: {
            ...(updated.settings || {}),
            private_page_logo_layout: savedLayout,
          },
        });
      } catch (err: unknown) {
        console.error('Erreur sauvegarde cadrage logo:', err);
        const ax = err as { response?: { data?: { error?: string; details?: string[] } } };
        const details = ax.response?.data?.details?.join(' ') || '';
        setLogoLayoutSaveError(
          [ax.response?.data?.error, details].filter(Boolean).join(' — ')
            || 'Impossible de sauvegarder le cadrage du logo'
        );
      } finally {
        setLogoLayoutSaving(false);
      }
    },
    [provider, onProviderPatch]
  );

  const handleLogoLayoutChange = (layout: PartnerLogoCircleLayout) => {
    const normalized = normalizePartnerLogoCircleLayout(layout);
    handleChange('private_page_logo_layout', normalized);
    if (logoLayoutSaveTimer.current) {
      clearTimeout(logoLayoutSaveTimer.current);
    }
    logoLayoutSaveTimer.current = setTimeout(() => {
      void persistLogoLayout(normalized);
    }, 500);
  };

  const handleSubmit = async () => {
    if (!provider) return;

    try {
      setSaving(true);
      setError('');
      
      const logoLayout = normalizePartnerLogoCircleLayout(
        formData.private_page_logo_layout ?? DEFAULT_PARTNER_LOGO_CIRCLE_LAYOUT
      );
      const payload: UpdateProviderRequest = {
        ...formData,
        slug: formData.slug?.trim() || null,
        notification_recipient_email:
          formData.notification_recipient_email?.trim() || null,
        private_page_logo_layout: logoLayout,
        settings: {
          ...((formData.settings as Record<string, unknown>) || {}),
          private_page_logo_layout: logoLayout,
        },
      };
      if (logoLayoutSaveTimer.current) {
        clearTimeout(logoLayoutSaveTimer.current);
        logoLayoutSaveTimer.current = null;
      }
      await persistLogoLayout(payload.private_page_logo_layout!);
      const updatedProvider = await apiService.updateProvider(provider.id, payload);
      onSave({
        ...updatedProvider,
        private_page_logo_layout: payload.private_page_logo_layout,
      });
      onClose();
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof UpdateProviderRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const interfaceTheme = ((formData.settings as Record<string, any>)?.interface_theme ||
    DEFAULT_INTERFACE_THEME) as ProviderInterfaceThemeSettings;

  const updateInterfaceTheme = (next: Partial<ProviderInterfaceThemeSettings>) => {
    setFormData((prev) => {
      const currentSettings = (prev.settings as Record<string, any>) || {};
      const currentTheme =
        (currentSettings.interface_theme as ProviderInterfaceThemeSettings) || DEFAULT_INTERFACE_THEME;

      return {
        ...prev,
        settings: {
          ...currentSettings,
          interface_theme: {
            ...currentTheme,
            ...next,
          },
        },
      };
    });
  };

  const dashboardMenuSettings = {
    matchings_enabled:
      (formData.settings as Record<string, any>)?.dashboard_menu_matchings_enabled !== false,
    linked_announcements_enabled:
      (formData.settings as Record<string, any>)?.dashboard_menu_linked_announcements_enabled !== false,
  };

  const updateDashboardMenuSetting = (
    key: 'dashboard_menu_matchings_enabled' | 'dashboard_menu_linked_announcements_enabled',
    enabled: boolean
  ) => {
    setFormData((prev) => {
      const currentSettings = (prev.settings as Record<string, any>) || {};
      return {
        ...prev,
        settings: {
          ...currentSettings,
          [key]: enabled,
        },
      };
    });
  };

  const encouragePartnerDepositEnabled =
    (formData.settings as Record<string, any>)?.encourage_partner_deposit_enabled === true;

  const updateEncouragePartnerDepositSetting = (enabled: boolean) => {
    setFormData((prev) => {
      const currentSettings = (prev.settings as Record<string, any>) || {};
      return {
        ...prev,
        settings: {
          ...currentSettings,
          encourage_partner_deposit_enabled: enabled,
        },
      };
    });
  };

  const accountlessRestitutionEnabled =
    (formData.settings as Record<string, any>)?.accountless_restitution_enabled === true;

  const updateAccountlessRestitutionSetting = (enabled: boolean) => {
    setFormData((prev) => {
      const currentSettings = (prev.settings as Record<string, any>) || {};
      return {
        ...prev,
        settings: {
          ...currentSettings,
          accountless_restitution_enabled: enabled,
        },
      };
    });
  };

  const deliveryModesSettings = useMemo(() => {
    const defaults = Object.fromEntries(
      PROVIDER_DELIVERY_MODE_OPTIONS.map((option) => [option.key, true])
    ) as Record<string, boolean>;
    const settings = (formData.settings as Record<string, any>) || {};
    const modes = settings.delivery_modes;
    if (!modes || typeof modes !== 'object') {
      return defaults;
    }
    for (const option of PROVIDER_DELIVERY_MODE_OPTIONS) {
      if (Object.prototype.hasOwnProperty.call(modes, option.key)) {
        defaults[option.key] = modes[option.key] !== false;
      }
    }
    return defaults;
  }, [formData.settings]);

  const updateDeliveryModeSetting = (key: string, enabled: boolean) => {
    setFormData((prev) => {
      const currentSettings = (prev.settings as Record<string, any>) || {};
      const currentModes = { ...(currentSettings.delivery_modes || {}) };
      currentModes[key] = enabled;
      return {
        ...prev,
        settings: {
          ...currentSettings,
          delivery_modes: currentModes,
        },
      };
    });
  };

  if (!provider) return null;

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Modifier le partenaire : {provider.name}
      </DialogTitle>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Informations générales" />
          <Tab label="Gestion des utilisateurs" />
          <Tab label="Suppression" sx={{ color: 'error.main' }} />
        </Tabs>
      </Box>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {activeTab === 0 && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Informations de base */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Informations de base
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nom du partenaire"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Slug URL publique"
              value={formData.slug ?? ''}
              onChange={(e) => handleChange('slug', e.target.value.toLowerCase())}
              placeholder="Ex. vieilles-charrues"
              helperText={
                formData.slug?.trim()
                  ? `URL publique : /fr/objets-trouves/${formData.slug.trim()}`
                  : 'Identifiant pour l’URL /objets-trouves/{slug}. Laissez vide pour conserver l’URL par ID.'
              }
              inputProps={{ maxLength: 120 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={loadingProviderTypes}>
              <InputLabel>Type de partenaire (base)</InputLabel>
              <Select
                value={formData.provider_type_id ?? ''}
                label="Type de partenaire (base)"
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const pt = dbProviderTypes?.provider_types?.find((t) => t.id === id);
                  setFormData((prev) => ({
                    ...prev,
                    provider_type_id: id,
                    type: pt?.slug ?? prev.type,
                  }));
                }}
              >
                {(dbProviderTypes?.provider_types ?? []).map((pt) => (
                  <MenuItem key={pt.id} value={pt.id}>
                    {pt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                label="Statut"
              >
                <MenuItem value="active">Actif</MenuItem>
                <MenuItem value="inactive">Inactif</MenuItem>
                <MenuItem value="pending_approval">En attente</MenuItem>
                <MenuItem value="suspended">Suspendu</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              rows={3}
            />
          </Grid>

          {/* Contact */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Informations de contact
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Site web"
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://example.com"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Téléphone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 700 }}>
              Notifications e-mail (partenaire)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Alertes de correspondance Sherlook Pro et copie des e-mails de nouveaux messages lorsque le destinataire est un compte du lieu.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.notification_emails_enabled !== false}
                  onChange={(_, c) => handleChange('notification_emails_enabled', c)}
                  color="primary"
                />
              }
              label="Activer les notifications e-mail automatiques"
            />
            <TextField
              fullWidth
              label="E-mail dédié aux notifications (optionnel)"
              type="email"
              value={formData.notification_recipient_email ?? ''}
              onChange={(e) => handleChange('notification_recipient_email', e.target.value)}
              disabled={formData.notification_emails_enabled === false}
              sx={{ mt: 1.5 }}
              helperText={
                formData.notification_emails_enabled === false
                  ? 'Désactivé : aucun e-mail automatique vers la boîte partenaire.'
                  : 'Si vide : utilisation de l’e-mail de contact ci-dessus, puis du compte administrateur du lieu.'
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 700 }}>
              Visibilité publique
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Les annonces déposées par le partenaire (objets trouvés) et celles déposées par les utilisateurs chez ce
              partenaire ne sont pas affichées sur le site public. Seul le matching permet la mise en relation.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.visible_on_frontend !== false}
                  onChange={(_, c) => handleChange('visible_on_frontend', c)}
                  color="primary"
                />
              }
              label="Affiché sur le frontend"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, ml: 4.5 }}>
              Désactivé : le partenaire est masqué du menu, du carousel accueil et des fiches publiques Sherlook Pro.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.private_announcements === true}
                  onChange={(_, c) => handleChange('private_announcements', c)}
                  color="primary"
                />
              }
              label="Annonces privées"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Mode lieux / stockage</InputLabel>
              <Select
                label="Mode lieux / stockage"
                value={formData.place_storage_mode_override ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  handleChange(
                    'place_storage_mode_override',
                    v === '' ? null : (v as 'combined' | 'split' | 'central')
                  );
                }}
              >
                <MenuItem value="">
                  Hériter du type
                  {provider.provider_type?.place_storage_mode
                    ? ` (${provider.provider_type.place_storage_mode})`
                    : ''}
                </MenuItem>
                <MenuItem value="combined">Combinés — festivals (boîtes par lieu)</MenuItem>
                <MenuItem value="split">Séparés — lieux de perte + stockage central</MenuItem>
                <MenuItem value="central">Central — stockage unique + boîtes (hôtel, cinéma)</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Effectif :{' '}
              {formData.place_storage_mode_override ||
                provider.place_storage_mode ||
                provider.provider_type?.place_storage_mode ||
                'combined'}
            </Typography>
          </Grid>

          {formData.private_announcements && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Page publique — fond plein écran
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Même visuel que l&apos;accueil (doudou) avec la couleur de fond ci-dessous. Visible sur
                l&apos;URL partenaire lorsque les annonces sont privées.
              </Typography>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <TextField
                  type="color"
                  label="Couleur de fond"
                  value={formData.private_page_background_color || '#EBC41D'}
                  onChange={(e) => handleChange('private_page_background_color', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 120 }}
                  disabled={saving}
                />
                <TextField
                  size="small"
                  label="Code hex"
                  value={formData.private_page_background_color || '#EBC41D'}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(v) || v === '') {
                      handleChange('private_page_background_color', v || '#EBC41D');
                    }
                  }}
                  inputProps={{ maxLength: 7 }}
                  sx={{ width: 120 }}
                  disabled={saving}
                />
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: formData.private_page_background_color || '#EBC41D',
                  }}
                  title="Aperçu couleur de fond"
                />
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 700 }}>
              Dépôt d&apos;annonce chez le partenaire
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Pour les annonces « objet trouvé » liées à ce partenaire, propose à l&apos;utilisateur de
              déposer l&apos;objet dans un ou plusieurs lieux du partenaire (accueil, consigne, etc.).
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={encouragePartnerDepositEnabled}
                  onChange={(_, checked) => updateEncouragePartnerDepositSetting(checked)}
                  color="primary"
                />
              }
              label="Inciter au dépôt chez le partenaire"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 700 }}>
              Restitution sans compte
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Sur les cartes objets trouvés du dashboard, ajoute un bouton pour envoyer une proposition
              de livraison par e-mail à un propriétaire identifié (appel téléphonique, etc.) sans qu&apos;il
              crée de compte. Il paie via Stripe ; le partenaire imprime ensuite le bon d&apos;expédition.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={accountlessRestitutionEnabled}
                  onChange={(_, checked) => updateAccountlessRestitutionSetting(checked)}
                  color="primary"
                />
              }
              label="Activer la restitution sans compte"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 700 }}>
              Dashboard partenaire (Sherlook Pro)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Affiche ou masque des entrées du menu latéral pour ce partenaire uniquement.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={dashboardMenuSettings.matchings_enabled}
                  onChange={(_, c) => updateDashboardMenuSetting('dashboard_menu_matchings_enabled', c)}
                  color="primary"
                />
              }
              label="Menu « Mes matchings »"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={dashboardMenuSettings.linked_announcements_enabled}
                  onChange={(_, c) =>
                    updateDashboardMenuSetting('dashboard_menu_linked_announcements_enabled', c)
                  }
                  color="primary"
                />
              }
              label="Menu « Annonces liées (clients) »"
              sx={{ display: 'block' }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 700 }}>
              Modes de livraison
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Active ou désactive les modes proposables pour les objets trouvés via ce partenaire.
              Les tarifs globaux restent définis dans « Tarifs livraison ».
            </Typography>
            {PROVIDER_DELIVERY_MODE_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.key}
                control={
                  <Switch
                    checked={deliveryModesSettings[option.key] !== false}
                    onChange={(_, checked) => updateDeliveryModeSetting(option.key, checked)}
                    color="primary"
                  />
                }
                label={option.label}
                sx={{ display: 'block' }}
              />
            ))}
          </Grid>

          {/* Logo */}
          <Grid item xs={12}>
            <LogoUpload
              providerId={provider.id}
              currentLogo={provider.logo}
              currentLogoUrl={provider.logo_url}
              onLogoChange={(logoUrl) => {
                setLogoUrlOverride(logoUrl);
              }}
              disabled={saving}
            />
          </Grid>

          {(logoUrlOverride ?? provider.logo_url) && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Logo dans le cercle — carrousel d'accueil &amp; page partenaire
              </Typography>
              {logoLayoutSaveError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {logoLayoutSaveError}
                </Alert>
              )}
              {logoLayoutSaving && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Enregistrement du cadrage…
                </Typography>
              )}
              <PartnerLogoCircleEditor
                logoUrl={resolveProviderLogoUrl(
                  logoUrlOverride ?? provider.logo_url,
                  provider.logo
                )}
                providerName={formData.name || provider.name}
                backgroundColor={formData.private_page_background_color || '#EBC41D'}
                layout={normalizePartnerLogoCircleLayout(
                  formData.private_page_logo_layout ?? DEFAULT_PARTNER_LOGO_CIRCLE_LAYOUT
                )}
                onChange={handleLogoLayoutChange}
                disabled={saving || logoLayoutSaving}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
              Adresse &amp; localisation
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ProviderAddressAutocompleteMap
              value={toProviderAddressFields(formData.address, provider)}
              onChange={(next) =>
                setFormData((prev) => ({
                  ...prev,
                  address: { ...next, name: next.name || '' },
                }))
              }
              logoUrl={logoUrlOverride ?? provider.logo_url}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Theme de couleurs
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Theme preset</InputLabel>
              <Select
                label="Theme preset"
                value={interfaceTheme.preset || 'custom'}
                onChange={(e) => {
                  const preset = String(e.target.value);
                  const found = THEME_PRESETS.find((p) => p.key === preset);
                  if (found) {
                    updateInterfaceTheme({ preset: found.key, ...found.colors });
                  } else {
                    updateInterfaceTheme({ preset: 'custom' });
                  }
                }}
              >
                {THEME_PRESETS.map((preset) => (
                  <MenuItem key={preset.key} value={preset.key}>
                    {preset.label}
                  </MenuItem>
                ))}
                <MenuItem value="custom">Personnalise</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                backgroundColor: interfaceTheme.background || DEFAULT_INTERFACE_THEME.background,
              }}
            >
              <Box display="flex" gap={1} mb={1.5}>
                <Box sx={{ width: 24, height: 24, borderRadius: 999, bgcolor: interfaceTheme.primary }} />
                <Box sx={{ width: 24, height: 24, borderRadius: 999, bgcolor: interfaceTheme.secondary }} />
              </Box>
              <Button size="small" variant="contained" sx={{ mr: 1 }}>
                Primaire
              </Button>
              <Button size="small" variant="outlined">
                Secondaire
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ minWidth: { sm: 170 } }}>
                Logo partenaire
              </Typography>
              {logoUrlOverride || provider.logo_url || provider.logo ? (
                <Box
                  component="img"
                  src={resolveProviderLogoUrl(
                    logoUrlOverride ?? provider.logo_url,
                    provider.logo
                  ) || undefined}
                  alt={`Logo ${provider.name}`}
                  sx={{
                    height: 80,
                    maxWidth: 220,
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: '#fff',
                    p: 1,
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucun logo pour ce partenaire.
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Astuce : utilise la pipette sur ce logo pour reprendre les couleurs de marque.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Couleur primaire"
              type="color"
              value={interfaceTheme.primary || DEFAULT_INTERFACE_THEME.primary}
              onChange={(e) => updateInterfaceTheme({ preset: 'custom', primary: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Couleur secondaire"
              type="color"
              value={interfaceTheme.secondary || DEFAULT_INTERFACE_THEME.secondary}
              onChange={(e) => updateInterfaceTheme({ preset: 'custom', secondary: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Fond global"
              type="color"
              value={interfaceTheme.background || DEFAULT_INTERFACE_THEME.background}
              onChange={(e) => updateInterfaceTheme({ preset: 'custom', background: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <ProviderPlacesSection
              providerId={provider.id}
              defaultAttributesTemplate={provider.provider_type?.default_attributes || {}}
              placeStorageMode={
                (formData.place_storage_mode_override ||
                  provider.place_storage_mode ||
                  provider.provider_type?.place_storage_mode ||
                  'combined') as 'combined' | 'split' | 'central'
              }
            />
          </Grid>

          {/* Tarification */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Tarification
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Frais mensuels (€)"
              type="number"
              value={formData.monthly_fee}
              onChange={(e) => handleChange('monthly_fee', e.target.value ? e.target.value.toString() : undefined)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

        </Grid>
        )}

        {activeTab === 1 && (
          <Box sx={{ mt: 2 }}>
            <ProviderUserManager
              providerId={provider.id}
              providerName={provider.name}
              onUserAdded={() => {}}
              onUserRemoved={() => {}}
            />
          </Box>
        )}

        {activeTab === 2 && (
          <ProviderDeletionSection
            providerId={provider.id}
            providerName={provider.name}
            onDeleted={() => {
              onClose();
              onDeleted?.();
            }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        
        {activeTab === 0 && (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
          </Button>
        )}
        
        {(activeTab === 1 || activeTab === 2) && (
          <Button 
            onClick={onClose} 
            variant="contained"
          >
            Fermer
          </Button>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default EditProviderDialog;
